import { Modal, Row, Col, Button } from "antd";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Home } from "../components";
import { ContactForm } from "../components/contactForm";
import Skill from "../components/skill";
import Project from "../components/project";
import Education from "../components/education";
import Experience from "../components/experience";
import SoftSkillList from "../components/softSkillList";
import { SoftSkillData, SoftSkillData1 } from "../constant/soft_skills";
import Carousel from "../animation/carousel";
import { ProjectData } from "../constant/project";
import TechMarquee from "../components/techMarquee";
import CertificateGallery from "../components/certificateGallery";
import { scrollToSection } from "../animation/smoothScroll";
import { useCountUp } from "../animation/scrollReveal";
import DevIllustrationLoop from "../components/devIllustrationLoop";

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(0);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    scrollToSection(0, 0);
  };

  const showModal = (index) => {
    setSelectedProject(index);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const showContactModal = () => {
    setIsContactModalOpen(true);
  };

  const handleContactCancel = () => {
    setIsContactModalOpen(false);
  };

  return (
    <div>
      {/* Hero Section */}
      <Section id="home">
        <Home showModal={showContactModal} />
      </Section>

      {/* Trusted Technologies Marquee */}
      <TechMarquee />

      {/* About Section */}
      <Section id="about">
        <AboutSection />
      </Section>

      {/* Skills Section */}
      <Section id="skills">
        <Skill />
        <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
          <Col xs={24} lg={12}>
            <SoftSkillList
              data={SoftSkillData}
              title="Core Soft Skills"
            />
          </Col>
          <Col xs={24} lg={12}>
            <SoftSkillList
              data={SoftSkillData1}
              title="Professional Traits"
            />
          </Col>
        </Row>
      </Section>

      {/* Projects Section */}
      <Section id="projects">
        <Project showModal={showModal} />
      </Section>

      {/* GitHub Activity Section */}
      <Section id="github" style={{ background: "rgba(0, 0, 0, 0.02)" }}>
        <GithubSection />
      </Section>

      {/* Experience Section */}
      <Section id="experience">
        <Experience />
      </Section>

      {/* Education Section */}
      <Section id="education">
        <Education />
      </Section>

      {/* Certificates Section */}
      <Section id="certificates" style={{ background: "rgba(0,0,0,0.02)" }}>
        <CertificateGallery />
      </Section>

      {/* Services Section */}
      <Section id="services">
        <ServicesSection />
      </Section>

      {/* Testimonials Section */}
      <Section id="testimonials" style={{ background: "rgba(8, 33, 58, 0.3)" }}>
        <TestimonialsSection />
      </Section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          onClick={scrollToTop}
          style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            width: "50px",
            height: "50px",
            borderRadius: "0px",
            background: "var(--bg-dark)",
            border: "2px solid var(--accent-color)",
            color: "white",
            fontSize: "20px",
            cursor: "pointer",
            boxShadow: "6px 6px 0px var(--accent-color)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          whileHover={{ translate: "-4px, -4px" }}
          whileTap={{ translate: "2px, 2px" }}
        >
          <i className="fa fa-arrow-up" />
        </motion.button>
      )}

      {/* Contact Section */}
      <Section id="contact">
        <ContactSection />
      </Section>
      {/* Footer */}
      <Footer />
      {/* Modals */}
      <ContactModal
        isModalOpen={isContactModalOpen}
        handleCancel={handleContactCancel}
      />
      <ProjectModal
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        project={ProjectData[selectedProject]}
      />
    </div>
  );
};

export default HomePage;

// Section wrapper component
const Section = ({ id, children, style }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8 }}
    style={{
      minHeight: id === "home" ? "100vh" : "auto",
      padding: id==="home"? "0" : "80px 5%",
      position: "relative",
      ...style,
    }}
  >
    {children}
  </motion.section>
);

// About Section Component
const AboutSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="header" style={{ textAlign: "center", marginBottom: 40 }}>
      <i className="fa fa-user title-icon" />
      ABOUT ME
    </div>
    <Row gutter={[48, 48]} align="middle">
      <Col xs={24} md={12}>
        <motion.div
          whileHover={{ x: 5 }}
          style={{
            background: "#ffffff",
            padding: 40,
            border: "1px solid var(--card-border)",
            boxShadow: "10px 10px 0px var(--bg-dark)",
          }}
        >
          <h2 style={{
            color: "var(--primary-color)",
            fontSize: "28px",
            marginBottom: "20px",
            fontWeight: 700
          }}>
            My Story & Passion
          </h2>
          <p style={{
            fontSize: "16px",
            lineHeight: "1.8",
            color: "var(--text-secondary)",
            marginBottom: "15px"
          }}>
            I didn't just stumble into web development; I fell in love with the challenge of bridging the physical and digital worlds.
            My journey into **IoT and Real-time systems** started when I realized how much impact live data can have—from monitoring worker safety in global supply chains to tracking carbon emissions in real-time.
          </p>
          <p style={{
            fontSize: "16px",
            lineHeight: "1.8",
            color: "var(--text-secondary)",
            marginBottom: "15px"
          }}>
            I specialize in the **MERN stack**, but my real expertise lies in building systems that stay "alive"—using **Socket.io** and **MQTT** to handle thousands of events per second without breaking a sweat.
          </p>
          <p style={{
            fontSize: "16px",
            lineHeight: "1.8",
            color: "var(--text-secondary)"
          }}>
            Whether it's an IoT alert system or a scalable dashboard, I build with two things in mind: **technical depth** and **business impact**. I don't just write code; I build tools that save time, money, and potentially lives.
          </p>
        </motion.div>
      </Col>
      <Col xs={24} md={12}>
        <Row gutter={[16, 16]}>
          {stats.map((stat, index) => (
            <Col xs={12} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
                style={{
                  background: "#ffffff",
                  padding: 30,
                  textAlign: "center",
                  border: "1px solid var(--card-border)",
                  boxShadow: "6px 6px 0px var(--primary-color)",
                }}
              >
                <StatValue value={stat.value} />
                <div style={{
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "1px"
                }}>
                  {stat.label}
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            width: "100%",
            maxWidth: 480,
            margin: "24px auto 0",
          }}
        >
          <DevIllustrationLoop />
        </motion.div>
      </Col>
    </Row>
  </motion.div>
);

// Services Section Component
const ServicesSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="header" style={{ textAlign: "center", marginBottom: 40 }}>
      <i className="fa fa-briefcase title-icon" />
      SERVICES
    </div>
    <Row gutter={[32, 32]}>
      {services.map((service, index) => (
        <Col xs={24} sm={12} lg={8} key={index}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            whileHover={{ y: -10 }}
            style={{
              background: "#ffffff",
              padding: 30,
              border: "1px solid var(--card-border)",
              boxShadow: "8px 8px 0px var(--bg-dark)",
              height: "100%",
              transition: "all 0.3s ease",
            }}
            className="service-card"
          >
            <div style={{
              fontSize: "48px",
              color: "var(--primary-color)",
              marginBottom: "20px",
              textAlign: "center"
            }}>
              <i className={service.icon} />
            </div>
            <h3 style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "var(--text-primary)",
              marginBottom: "15px",
              textAlign: "center"
            }}>
              {service.title}
            </h3>
            <p style={{
              fontSize: "15px",
              lineHeight: "1.7",
              color: "var(--text-secondary)",
              textAlign: "center"
            }}>
              {service.description}
            </p>
          </motion.div>
        </Col>
      ))}
    </Row>
  </motion.div>
);

// Testimonials Section Component
const TestimonialsSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="header" style={{ textAlign: "center", marginBottom: 40 }}>
      <i className="fa fa-quote-left title-icon" />
      TESTIMONIALS
    </div>
    <Row gutter={[32, 32]}>
      {testimonials.map((testimonial, index) => (
        <Col xs={24} md={12} key={index}>
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2, duration: 0.6 }}
            whileHover={{ y: -5 }}
            style={{
              background: "#ffffff",
              padding: 30,
              border: "1px solid var(--card-border)",
              boxShadow: "10px 10px 0px var(--accent-color)",
              height: "100%",
              transition: "all 0.3s ease",
              position: "relative"
            }}
          >
            <div style={{
              fontSize: "48px",
              color: "rgba(23, 114, 184, 0.2)",
              position: "absolute",
              top: "20px",
              left: "20px"
            }}>
              "
            </div>
            <p style={{
              fontSize: "15px",
              lineHeight: "1.8",
              color: "var(--text-secondary)",
              fontStyle: "italic",
              marginTop: "30px",
              marginBottom: "20px"
            }}>
              {testimonial.text}
            </p>
            <div style={{ display: "flex", alignItems: "center", marginTop: "20px" }}>
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "0px",
                background: "var(--bg-dark)",
                border: "2px solid var(--accent-color)",
                boxShadow: "4px 4px 0px var(--accent-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "24px",
                fontWeight: 900,
                marginRight: "20px"
              }}>
                {testimonial.name.charAt(0)}
              </div>
              <div>
                <div style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  marginBottom: "3px"
                }}>
                  {testimonial.name}
                </div>
                <div style={{
                  fontSize: "13px",
                  color: "var(--primary-color)"
                }}>
                  {testimonial.position}
                </div>
              </div>
            </div>
            <div style={{
              marginTop: "15px",
              color: "var(--accent-color)",
              fontSize: "18px"
            }}>
              {"★".repeat(5)}
            </div>
          </motion.div>
        </Col>
      ))}
    </Row>
  </motion.div>
);

// GitHub Section Component
const GithubSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="header" style={{ textAlign: "center", marginBottom: 40 }}>
      <i className="fa-brands fa-github title-icon" />
      GITHUB ACTIVITY
    </div>
    <Row gutter={[32, 32]} justify="center">
      <Col xs={24} lg={16}>
        <div style={{
          background: "#ffffff",
          padding: "30px",
          border: "1px solid var(--card-border)",
          boxShadow: "10px 10px 0px var(--bg-dark)",
          textAlign: "center"
        }}>
          <h3 style={{ fontWeight: 800, marginBottom: "20px" }}>Open Source Contributions & Activity</h3>
          <img
            src="https://ghchart.rshah.org/4A9FD8/NewDivide41194"
            alt="Github Contribution Chart"
            style={{ width: "100%", marginBottom: "20px", filter: "grayscale(0.2)" }}
          />
          <Row gutter={[16, 16]} style={{ marginTop: "30px" }}>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: "24px", fontWeight: 900, color: "var(--accent-color)" }}>500+</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", textTransform: "uppercase" }}>Commits</div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: "24px", fontWeight: 900, color: "var(--accent-color)" }}>20+</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", textTransform: "uppercase" }}>Repositories</div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: "24px", fontWeight: 900, color: "var(--accent-color)" }}>50+</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", textTransform: "uppercase" }}>Stars</div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: "24px", fontWeight: 900, color: "var(--accent-color)" }}>15+</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", textTransform: "uppercase" }}>Pull Requests</div>
            </Col>
          </Row>
          <div style={{ marginTop: "40px" }}>
            <Button size="large" type="primary" style={{ borderRadius: "0px", fontWeight: "bold" }}>
              <a href="https://github.com/NewDivide41194" target="_blank" rel="noreferrer" style={{ color: "white", textDecoration: "none" }}>
                View Full Profile on GitHub
              </a>
            </Button>
          </div>
        </div>
      </Col>
    </Row>
  </motion.div>
);

// Contact Section Component
const ContactSection = () => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
    style={{
      maxWidth: "800px",
      margin: "0 auto",
    }}
  >
    <div className="header" style={{ textAlign: "center", marginBottom: 40 }}>
      <i className="fa fa-envelope title-icon" />
      GET IN TOUCH
    </div>
    <motion.div
      whileHover={{ scale: 1.01 }}
      style={{
        background: "#ffffff",
        padding: 40,
        border: "2px solid var(--bg-dark)",
        boxShadow: "12px 12px 0px var(--bg-dark)",
      }}
    >
      <ContactForm />
    </motion.div>
    <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
      {contactInfo.map((info, index) => (
        <Col xs={24} sm={8} key={index}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            style={{
              textAlign: "center",
              padding: "20px",
            }}
          >
            <div style={{
              fontSize: "36px",
              color: "var(--primary-color)",
              marginBottom: "10px"
            }}>
              <i className={info.icon} />
            </div>
            <div style={{
              fontSize: "14px",
              color: "var(--text-muted)",
              marginBottom: "5px",
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}>
              {info.label}
            </div>
            <div style={{
              fontSize: "15px",
              color: "var(--text-secondary)",
              fontWeight: 500
            }}>
              {info.value}
            </div>
          </motion.div>
        </Col>
      ))}
    </Row>
  </motion.div>
);

const ContactModal = ({ isModalOpen, handleCancel }) => {
  return (
    <Modal
      footer={null}
      onCancel={handleCancel}
      title="CONTACT ME"
      open={isModalOpen}
    >
      <ContactForm />
    </Modal>
  );
};

const ProjectModal = ({ isModalOpen, handleCancel, project }) => {
  return (
    <Modal
      footer={null}
      onCancel={handleCancel}
      title={project?.title || "PROJECT DETAILS"}
      open={isModalOpen}
      width={800}
    >
      {project?.screen && <Carousel screen={project.screen} />}
    </Modal>
  );
};

const StatValue = ({ value }) => (
  <div
    ref={useCountUp(value)}
    style={{
      fontSize: "42px",
      fontWeight: 900,
      color: "var(--bg-dark)",
      marginBottom: "5px"
    }}
  >
    {value}
  </div>
);

const stats = [
  { value: "6+", label: "Years Experience" },
  { value: "30+", label: "Projects Completed" },
  { value: "10+", label: "Happy Clients" },
  { value: "10+", label: "Technologies" },
];

const services = [
  {
    icon: "fa fa-code",
    title: "Web Development",
    description: "Building responsive and performant web applications using modern frameworks and best practices."
  },
  {
    icon: "fa fa-mobile",
    title: "Responsive Design",
    description: "Creating seamless experiences across all devices with mobile-first design principles."
  },
  {
    icon: "fa fa-database",
    title: "Backend Development",
    description: "Developing robust APIs and server-side solutions with scalable architecture."
  },
  {
    icon: "fa fa-paint-brush",
    title: "UI/UX Design",
    description: "Designing intuitive interfaces with focus on user experience and visual aesthetics."
  },
  {
    icon: "fa fa-rocket",
    title: "Performance Optimization",
    description: "Optimizing applications for speed, efficiency, and better user engagement."
  },
  {
    icon: "fa fa-cogs",
    title: "AI System Integration",
    description: "Integrating AI service, third-party services and APIs to enhance functionality."
  },
];

const contactInfo = [
  { icon: "fa fa-envelope", label: "Email", value: "heinhtet25944@gmail.com" },
  { icon: "fa fa-phone", label: "Phone", value: "+66 0988477743" },
  { icon: "fa fa-map-marker", label: "Location", value: "Bangkok, Thailand" },
];

const testimonials = [
  {
    name: "Ko Ko Win",
    position: "CEO, Kumo Solutions",
    text: "Working with Hein was an absolute pleasure. His technical expertise and attention to detail resulted in a product that exceeded our expectations. Highly recommended!"
  },
  {
    name: "Nick Nguyen",
    position: "Team Lead, VIATICK",
    text: "Hein's ability to understand complex requirements and translate them into elegant solutions is remarkable. The project was delivered on time and with high quality."
  },
  {
    name: "Sandar Nyunt",
    position: "HR, Kumo Solutions",
    text: "Professional, reliable, and skilled. Hein delivered a robust application with clean code and excellent performance. Looking forward to future collaborations!"
  },
  {
    name: "Ana Maria Soto Bernal",
    position: "Director of Impact & Innovation, ISSARA INSTITUTE",
    text: "The website Hein built for us not only looks amazing but also performs exceptionally well. Our conversion rates have increased significantly since launch."
  },
];

// Footer Component
const Footer = () => (
  <footer style={{
    background: "var(--bg-dark)",
    borderTop: "6px solid var(--accent-color)",
    padding: "60px 5%",
    marginTop: "80px",
    color: "#ffffff"
  }}>
    <Row gutter={[32, 32]}>
      <Col xs={24} md={8}>
        <h3 style={{
          fontSize: "28px",
          fontWeight: 900,
          color: "var(--primary-color)",
          marginBottom: "15px",
          textTransform: "uppercase",
          letterSpacing: "2px"
        }}>
          Hein Htet
        </h3>
        <p style={{
          color: "var(--text-light)",
          fontSize: "14px",
          lineHeight: "1.6"
        }}>
          Full-Stack Web Developer passionate about creating amazing digital experiences.
        </p>
        <div style={{ marginTop: "20px", fontSize: "24px" }}>
          <a href="mailto:heinhtet25944@gmail.com" target="_blank" rel="noreferrer" style={{ marginRight: "20px", color: "#ffffff" }}>
            <i className="fa fa-envelope" />
          </a>
          <a href="https://www.linkedin.com/in/hein-htet-2a34241a3" target="_blank" rel="noreferrer" style={{ marginRight: "20px", color: "#ffffff" }}>
            <i className="fa-brands fa-linkedin" />
          </a>
          <a href="https://github.com/NewDivide41194" target="_blank" rel="noreferrer" style={{ marginRight: "20px", color: "#ffffff" }}>
            <i className="fa-brands fa-github" />
          </a>
          <a href="https://wa.me/66988477743" target="_blank" rel="noreferrer" style={{ color: "#ffffff" }}>
            <i className="fa-brands fa-whatsapp" />
          </a>
        </div>
      </Col>
      <Col xs={24} md={8}>
        <h4 style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "var(--text-light)",
          marginBottom: "15px",
        }}>
          Quick Links
        </h4>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {["home", "about", "skills", "projects", "education", "certificates", "contact"].map((link) => (
            <a
              key={link}
              href={`#${link}`}
              style={{
                color: "var(--text-light)",
                fontSize: "14px",
                textTransform: "capitalize",
                transition: "color 0.3s ease"
              }}
              onMouseEnter={(e) => e.target.style.color = "var(--primary-color)"}
              onMouseLeave={(e) => e.target.style.color = "var(--text-light)"}
            >
              {link}
            </a>
          ))}
        </div>
      </Col>
      <Col xs={24} md={8}>
        <h4 style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "var(--text-light)",
          marginBottom: "15px"
        }}>
          Technologies
        </h4>
        <p style={{
          color: "var(--text-light)",
          fontSize: "14px",
          lineHeight: "1.8"
        }}>
          React • Node.js • MySQL<br />
          JavaScript • TypeScript<br />
          HTML5 • CSS3 • REST APIs
        </p>
      </Col>
    </Row>
    <div style={{
      textAlign: "center",
      marginTop: "40px",
      paddingTop: "20px",
      borderTop: "1px solid rgba(23, 114, 184, 0.15)",
      color: "var(--text-muted)",
      fontSize: "14px"
    }}>
      © {new Date().getFullYear()} Hein Htet. All rights reserved. Built with ❤️ and React
    </div>
  </footer>
);
